/**
 * Auth/session KV that outlives a tab. After a domain switch, Chrome
 * bounce-tracking can treat origin localStorage as session-only; IndexedDB
 * is the same-origin backup (not sent on requests, unlike cookies).
 */
const DB_NAME = "bb-durable-kv";
const STORE = "kv";
const memory = new Map<string, string>();

function safeLocalGet(key: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalSet(key: string, value: string): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode / quota */
  }
}

function safeLocalRemove(key: string): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbGet(key: string): Promise<string | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => {
        const v = req.result;
        resolve(typeof v === "string" ? v : null);
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbSet(key: string, value: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

async function idbDel(key: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

export const durableKvStorage = {
  async getItem(key: string): Promise<string | null> {
    if (memory.has(key)) return memory.get(key) ?? null;
    const fromLs = safeLocalGet(key);
    if (fromLs != null) {
      memory.set(key, fromLs);
      return fromLs;
    }
    const fromIdb = await idbGet(key);
    if (fromIdb != null) {
      memory.set(key, fromIdb);
      safeLocalSet(key, fromIdb);
      return fromIdb;
    }
    return null;
  },
  setItem(key: string, value: string): void {
    memory.set(key, value);
    safeLocalSet(key, value);
    void idbSet(key, value);
  },
  removeItem(key: string): void {
    memory.delete(key);
    safeLocalRemove(key);
    void idbDel(key);
  },
};

export function requestPersistentStorage(): void {
  try {
    void navigator.storage?.persist?.();
  } catch {
    /* unsupported */
  }
}
